<template>
  <div class="ror-details">
    <section class="ror-details__grid">
      <StField label="Points">
        <StNumberInput
          :model-value="modelValue.points"
          :min="0"
          @update:model-value="patch({ points: $event ?? 0 })"
        />
      </StField>
      <StCheckbox
        :model-value="modelValue.legends ?? false"
        @update:model-value="patch({ legends: $event })"
      >
        Legends
      </StCheckbox>
    </section>

    <section class="ror-details__grid">
      <StField group label="Source armies" hint="Armies that provide the regiment's units">
        <KeywordsInput
          :model-value="modelValue.sourceArmies"
          raw
          noun="Army"
          suggestion-scope="army"
          placeholder="e.g. Sons of Behemat"
          @update:model-value="patch({ sourceArmies: $event })"
        />
      </StField>
      <StField group label="Allowed armies" hint="Armies that can include this regiment">
        <KeywordsInput
          :model-value="modelValue.selectableIn"
          raw
          noun="Army"
          suggestion-scope="army"
          placeholder="e.g. Stormcast Eternals"
          @update:model-value="patch({ selectableIn: $event })"
        />
      </StField>
    </section>

    <section class="form-section">
      <div class="st-eyebrow">Regiment Abilities</div>
      <UpgradeGroupForm
        hide-name
        rules-position="top"
        :model-value="regimentUpgrade"
        @update:model-value="updateUpgrade"
      />
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { IRegimentOfRenown, IUpgrade } from '@scrolldex/shared';
import { makeRegimentOfRenownUpgrade } from '@scrolldex/shared';
import KeywordsInput from '../KeywordsInput.vue';
import { StCheckbox, StField, StNumberInput } from '../ui/index.js';
import UpgradeGroupForm from './UpgradeGroupForm.vue';

const props = defineProps<{ modelValue: IRegimentOfRenown }>();
const emit = defineEmits<{ (e: 'update:modelValue', value: IRegimentOfRenown): void }>();

const regimentUpgrade = computed(
  () => props.modelValue.upgrades[0] ?? makeRegimentOfRenownUpgrade(props.modelValue.name),
);

function patch(part: Partial<IRegimentOfRenown>) {
  emit('update:modelValue', { ...props.modelValue, ...part });
}

function updateUpgrade(upgrade: IUpgrade) {
  patch({
    upgrades: [{ ...upgrade, name: `Regiment of Renown: ${props.modelValue.name}` }],
  });
}
</script>

<style scoped>
.ror-details {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-4);
}
.ror-details__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--st-space-3);
  align-items: start;
}
.form-section {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
}
@media (max-width: 780px) {
  .ror-details__grid {
    grid-template-columns: 1fr;
  }
}
</style>
